import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useBrowserNotificationsForWhatsApp } from '@/hooks/useBrowserNotificationsForWhatsApp';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { getFullPhoneE164 } from '@/lib/utils';
import { createPageUrl } from '@/utils';

import { ChatCore } from '@/features/chat/components/ChatCore';
import { InboxHeader } from '@/features/chat/headers/InboxHeader';
import { InboxLayout } from '@/features/chat/layouts/InboxLayout';
import type { ConversationItem } from '@/features/chat/hooks/useInboxConversations';

export default function WhatsApp() {
  const navigate = useNavigate();
  const { data: currentAppUser } = useCurrentAppUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedContact, setSelectedContact] = useState<ConversationItem | null>(null);

  // Browser notifications
  useBrowserNotificationsForWhatsApp(selectedContact?.id);

  // Auto-select contact from URL params
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (!contactId || !currentAppUser) return;

    const autoSelect = async () => {
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_phone, primary_phone_country_code')
        .eq('id', contactId)
        .single();
      if (data) {
        setSelectedContact({
          id: data.id,
          full_name: [data.first_name, data.last_name].filter(Boolean).join(' ') || 'Sem nome',
          phone: getFullPhoneE164(data.primary_phone, data.primary_phone_country_code),
          entityType: 'contact',
          lastMessage: null,
          lastMessageTime: null,
          lastMessageDirection: null,
          unreadCount: 0,
          hasMySentMessages: false,
          isAssignedToMe: false,
        });
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('contactId');
          return next;
        }, { replace: true });
      }
    };
    autoSelect();
  }, [currentAppUser, searchParams]);

  // Navigate to contact/lead detail
  const handleClickContact = useCallback(() => {
    if (!selectedContact) return;
    if (selectedContact.entityType === 'contact') {
      navigate(createPageUrl('ContactDetail') + `?id=${selectedContact.id}`);
    } else {
      navigate(createPageUrl('LeadDetail') + `?id=${selectedContact.id}`);
    }
  }, [selectedContact, navigate]);

  // Initiate call
  const handleCall = useCallback(() => {
    if (!selectedContact?.phone) return;
    window.dispatchEvent(new CustomEvent('initiate-call', {
      detail: {
        contactId: selectedContact.id,
        phoneNumber: selectedContact.phone,
        contactName: selectedContact.full_name,
      },
    }));
  }, [selectedContact]);

  return (
    <div className="h-full bg-background p-2 overflow-hidden">
      <InboxLayout
        selectedContactId={selectedContact?.id || null}
        onSelectContact={setSelectedContact}
      >
        {selectedContact && (
          <ChatCore
            contactId={selectedContact.entityType === 'contact' ? selectedContact.id : ''}
            leadId={selectedContact.entityType === 'lead' ? selectedContact.id : undefined}
            contactPhone={selectedContact.phone}
            leadPhone={selectedContact.entityType === 'lead' ? selectedContact.phone : undefined}
            className="rounded-2xl"
            header={
              <InboxHeader
                contactName={selectedContact.full_name}
                contactPhone={selectedContact.phone}
                onCall={handleCall}
                onClickContact={handleClickContact}
                showBackButton
              />
            }
          />
        )}
      </InboxLayout>
    </div>
  );
}
